export const INVOICE_BALANCE_STATUS = Object.freeze({
    NATURALLY_BALANCED: "NATURALLY_BALANCED",
    UNEXPLAINED_VARIANCE: "UNEXPLAINED_VARIANCE",
});

export const CONVERSION_WARNING_CODES = Object.freeze([
    "MISSING_EXCHANGE_RATE",
    "MISSING_SETTLEMENT_RATE",
    "MIXED_CURRENCY_GRAND_TOTAL_UNRELIABLE",
    "GRAND_TOTAL_CURRENCY_UNRELIABLE",
]);

const CONVERSION_WARNING_CODE_SET = new Set(CONVERSION_WARNING_CODES);

export const isConversionWarning = (warning) => Boolean(warning && CONVERSION_WARNING_CODE_SET.has(warning.code));

export const hasInvoiceConversionWarning = (summary = {}) => {
    const warnings = Array.isArray(summary?.warnings) ? summary.warnings : [];
    if (warnings.length > 0) return warnings.some(isConversionWarning);
    return summary?.has_conversion_warning === true || summary?.has_conversion_warnings === true;
};

export const formatSignedAmount = (value, options = {}) => {
    if (value === null || value === undefined || value === "") return "-";
    const numericValue = Number(value);
    if (!Number.isFinite(numericValue)) return "-";
    const { minimumFractionDigits = 2, maximumFractionDigits = 6 } = options;
    const sign = numericValue > 0 ? "+" : "";
    return `${sign}${numericValue.toLocaleString(undefined, {
        minimumFractionDigits,
        maximumFractionDigits,
    })}`;
};

const isNonZeroFiniteAmount = (value) => value !== null
    && value !== undefined
    && value !== ""
    && Number.isFinite(Number(value))
    && Number(value) !== 0;

export const getInvoiceBalancePresentation = (summary = {}) => {
    const isSupportedStatus = (status) => Object.values(INVOICE_BALANCE_STATUS).includes(status);
    const globalStatus = isSupportedStatus(summary.balance_status) ? summary.balance_status : null;
    if (!globalStatus) {
        return {
            status: null,
            color: "#faad14",
            annotation: "金额平衡未评估，暂无可信结论",
        };
    }
    const status = isSupportedStatus(summary.invoice_balance_status)
        ? summary.invoice_balance_status
        : globalStatus;

    if (status === INVOICE_BALANCE_STATUS.UNEXPLAINED_VARIANCE) {
        const variance = summary.unexplained_variance ?? summary.header_variance;
        return {
            status,
            color: "#ff4d4f",
            annotation: isNonZeroFiniteAmount(variance)
                ? `金额不匹配 ${formatSignedAmount(variance)}，请检查数据`
                : "金额不匹配，请检查数据",
        };
    }

    return {
        status: INVOICE_BALANCE_STATUS.NATURALLY_BALANCED,
        color: isNonZeroFiniteAmount(summary.header_variance) ? "#52c41a" : undefined,
        annotation: isNonZeroFiniteAmount(summary.header_variance)
            ? `舍入尾差 ${formatSignedAmount(summary.header_variance)}（系统已自动处理）`
            : null,
    };
};

export const getSectionBalancePresentation = (sectionBalance = {}) => {
    const status = sectionBalance?.section_balance_status;
    if (status === INVOICE_BALANCE_STATUS.UNEXPLAINED_VARIANCE) {
        return {
            status,
            color: "#ff4d4f",
            annotation: isNonZeroFiniteAmount(sectionBalance.sub_variance)
                ? `金额不匹配 ${formatSignedAmount(sectionBalance.sub_variance)}，请检查数据`
                : "金额不匹配，请检查数据",
        };
    }

    if (status === INVOICE_BALANCE_STATUS.NATURALLY_BALANCED) {
        return {
            status,
            color: isNonZeroFiniteAmount(sectionBalance.sub_variance) ? "#52c41a" : undefined,
            annotation: isNonZeroFiniteAmount(sectionBalance.sub_variance)
                ? `舍入尾差 ${formatSignedAmount(sectionBalance.sub_variance)}（系统已自动处理）`
                : null,
        };
    }

    return {
        status: null,
        color: "#faad14",
        annotation: null,
    };
};
